/**
 * Ruft `run`, sobald der Main-Thread durchatmet: zwei Bilder abwarten (die
 * Seite ist gemalt), dann auf Leerlauf — höchstens `maxWait` ms, damit nichts
 * hängen bleibt, wenn der Leerlauf nie kommt.
 *
 * Für Einblendungen, die sonst in teure Arbeit fallen: Beim Wechsel des
 * Lebensbereichs rendern Sidebar, Titelleiste und Seite neu, die Farben des
 * Bereichs werden neu berechnet — eine Einblendung, die mitten darin startet,
 * ruckelt (25.09.2026).
 *
 * Gibt eine Abbruch-Funktion zurück.
 *
 * @example
 * useEffect(() => whenCalm(() => controls.start('shown')), [])
 */
export function whenCalm(run: () => void, { maxWait = 250 }: { maxWait?: number } = {}): () => void {
  let cancelled = false
  let frame = 0
  let idle: number | undefined
  let timer: ReturnType<typeof setTimeout> | undefined

  const fire = () => {
    if (!cancelled) run()
  }

  frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(() => {
      if (cancelled) return
      if (typeof requestIdleCallback === 'function') {
        idle = requestIdleCallback(fire, { timeout: maxWait })
      } else {
        // Safari kennt keinen Leerlauf-Rückruf — nach zwei Bildern ist das
        // Gröbste gemalt, der Rest der Arbeit bekommt noch einen Takt.
        timer = setTimeout(fire, 0)
      }
    })
  })

  return () => {
    cancelled = true
    cancelAnimationFrame(frame)
    if (idle !== undefined && typeof cancelIdleCallback === 'function') cancelIdleCallback(idle)
    if (timer !== undefined) clearTimeout(timer)
  }
}
